import React, { useState, useEffect } from "react";
import { Card, Form, Tabs, Tab, InputGroup, Button, Collapse, Accordion } from "react-bootstrap";
import SegmentedControl from "./SegmentedControl";

const InputForm = ({
  formData,
  setFormData,
  activeTab,
  handleTabChange,
  percentageValue,
  setPercentageValue,
  details,
  amortizationTab,
  setAmortizationTab,
  amortizationPercentage,
  setAmortizationPercentage,
  handleEgenInsatsChange,
  isEgenInsatsManuallyEdited,
  resetEgenInsats,
  highlightField,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(null); // Start as null to detect initial load
  const [userToggledInMobile, setUserToggledInMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      const wasMobile = isMobile;
      
      setIsMobile(mobile);
      
      // Handle initial load or mode transitions
      if (wasMobile === null || mobile !== wasMobile) {
        if (!mobile) {
          // Desktop mode - always expand
          setIsCollapsed(false);
          setUserToggledInMobile(false);
        } else {
          // Mobile mode - collapse only if user hasn't manually toggled
          if (wasMobile === null || !userToggledInMobile) {
            setIsCollapsed(true);
          }
        }
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile, userToggledInMobile]);

  const toggleCollapse = () => {
    if (isMobile) {
      setIsCollapsed(!isCollapsed);
      setUserToggledInMobile(true);
    }
  };
  const handleBlur = (e) => {
    const { name } = e.target;

    // Skip formatting for specific fields
    const skipFormattingFields = [
      "utgangspris",
      "taxeringsvarde",
      "pantbrev",
      "monthlyCost",
      "driftkostnad",
      "egenInsats",
      "amortering",
    ];
    if (skipFormattingFields.includes(name)) {
      return; // Do nothing for these fields
    }

    const formattedValue = parseFloat(formData[name]).toFixed(2);
    setFormData({
      ...formData,
      [name]: isNaN(formattedValue) ? "" : formattedValue,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for checkbox inputs
    if (name === "isDebtToIncomeHigh") {
      setFormData({
        ...formData,
        [name]: e.target.checked,
      });
      return;
    }

    // Sanitize input in real-time: Allow only numbers, dots, and commas
    const sanitizedValue = value
      .replace(/[^0-9.,]/g, "") // Remove invalid characters
      .replace(",", "."); // Replace comma with dot for consistency

    // Prevent multiple dots
    if ((sanitizedValue.match(/\./g) || []).length > 1) return;

    // Special handling for egenInsats
    if (name === "egenInsats") {
      handleEgenInsatsChange(sanitizedValue);
    } else {
      setFormData({
        ...formData,
        [name]: sanitizedValue,
      });
    }
  };

  const handlePropertyTypeChange = (propertyType) => {
    const newFormData = { ...formData, propertyType };
    
    // Clear fields that are not relevant to the new property type
    if (propertyType === "bostadsratt") {
      // Clear villa-specific fields when switching to bostadsrätt
      newFormData.taxeringsvarde = "";
      newFormData.pantbrev = "";
    }
    // Note: we keep monthlyCost (driftkostnad/avgift) as it's used for both types
    
    setFormData(newFormData);
  };

  const formatCurrency = (value) => {
    if (isNaN(value)) return value;
    return new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK" })
      .format(value)
      .replace("kr", "")
      .trim();
  };

  const getAmortizationInfo = () => {
    if (!details || !details.ltvRatio) return null;
    
    const ltvRatio = details.ltvRatio;
    const ltvAmortizationRate = details.ltvAmortizationRate || 0;
    const calculatedAmortering = details.calculatedAmortering || 0;

    // LTV rule text (April 1, 2026: debt-to-income rule removed)
    let ltvRuleText = "";
    if (ltvRatio <= 50) {
      ltvRuleText = "Ingen amortering (belåningsgrad ≤50%)";
    } else if (ltvRatio <= 70) {
      ltvRuleText = "1% amortering per år (belåningsgrad 50-70%)";
    } else {
      ltvRuleText = "2% amortering per år (belåningsgrad >70%)";
    }

    return {
      ltvRatio: ltvRatio.toFixed(1),
      ltvAmortizationRate: (ltvAmortizationRate * 100).toFixed(1),
      totalAmortizationRate: (ltvAmortizationRate * 100).toFixed(1),
      calculatedAmortering,
      ltvRuleText
    };
  };

  const amortInfo = getAmortizationInfo();

  const getValidationWarning = () => {
    if (!amortInfo || !details) return null;
    
    const requiredRate = details.requiredAmortizationRate || 0;
    const remainingAmount = details.utgangspris - (
      activeTab === "percentage" 
        ? (details.utgangspris * percentageValue) / 100 
        : parseFloat(formData.egenInsats || 0)
    );
    
    let currentAmortization = 0;
    if (amortizationTab === "percentage") {
      currentAmortization = (remainingAmount * parseFloat(amortizationPercentage)) / 100 / 12;
    } else {
      currentAmortization = parseFloat(formData.amortering) || 0;
    }
    
    const minimumRequired = amortInfo.calculatedAmortering;
    
    // Only show warning if current amount is less than minimum (with small tolerance for rounding)
    if (currentAmortization < minimumRequired && minimumRequired > 0) {
      return "Varning: Det valda beloppet kan vara lägre än vad som krävs enligt svenska regler.";
    }
    
    return null;
  };

  return (
    <div>
      <div>
        <h6 
          onClick={toggleCollapse}
          className={isMobile ? "sidebar-title-mobile" : ""}
          style={isMobile ? { cursor: 'pointer' } : {}}
        >
          Detaljer kring bostaden
          {isMobile && (
            <span className="ms-2">
              {isCollapsed ? '▼' : '▲'}
            </span>
          )}
        </h6>
        <Collapse in={!isCollapsed}>
          <div>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="mb-2">Typ av bostad</Form.Label>
            <div className="row g-2">
              <div className="col-6">
                <input 
                  type="radio" 
                  className="btn-check" 
                  name="propertyType" 
                  id="villa-card" 
                  value="villa"
                  checked={formData.propertyType === "villa"}
                  onChange={() => handlePropertyTypeChange("villa")}
                  autoComplete="off"
                />
                <label 
                  className={`btn property-type-card h-100 w-100 ${formData.propertyType === "villa" ? "btn-primary" : "btn-outline-secondary"}`} 
                  htmlFor="villa-card"
                >
                  <div className="text-center">
                    <div className="property-icon mb-2">🏠</div>
                    <div className="fw-bold small">Villa</div>
                    <div className="fw-bold small">Äganderätt</div>
                  </div>
                </label>
              </div>
              <div className="col-6">
                <input 
                  type="radio" 
                  className="btn-check" 
                  name="propertyType" 
                  id="bostadsratt-card" 
                  value="bostadsratt"
                  checked={formData.propertyType === "bostadsratt"}
                  onChange={() => handlePropertyTypeChange("bostadsratt")}
                  autoComplete="off"
                />
                <label 
                  className={`btn property-type-card h-100 w-100 ${formData.propertyType === "bostadsratt" ? "btn-primary" : "btn-outline-secondary"}`} 
                  htmlFor="bostadsratt-card"
                >
                  <div className="text-center">
                    <div className="property-icon mb-2">🏢</div>
                    <div className="fw-bold small">Bostadsrätt</div>
                  </div>
                </label>
              </div>
            </div>
          </Form.Group>

          <Form.Label>Utgångspris</Form.Label>
          <InputGroup className="mb-2">
            <Form.Control
              type="text" // Use "text" to allow sanitization
              name="utgangspris"
              value={formData.utgangspris || ""}
              onChange={handleInputChange}
              onBlur={handleBlur}
            />
            <InputGroup.Text>Kr</InputGroup.Text>
          </InputGroup>

          {formData.propertyType === "villa" && (
            <>
              <Form.Label>Taxeringsvärde</Form.Label>
              <InputGroup className="mb-2">
                <Form.Control
                  type="text"
                  name="taxeringsvarde"
                  value={formData.taxeringsvarde || ""}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                />
                <InputGroup.Text>Kr</InputGroup.Text>
              </InputGroup>
              <div className={`mb-2 ${highlightField === 'pantbrev' ? 'field-highlight' : ''}`} style={{
                padding: '0.5rem',
                borderRadius: '6px'
              }}>
                <Form.Label>Pantbrev</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    name="pantbrev"
                    value={formData.pantbrev || ""}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                  />
                  <InputGroup.Text>Kr</InputGroup.Text>
                </InputGroup>
              </div>
              <div className={`mb-2 ${highlightField === 'driftkostnad' ? 'field-highlight' : ''}`} style={{
                padding: '0.5rem',
                borderRadius: '6px'
              }}>
                <Form.Label>Driftkostnad per månad</Form.Label>
                <InputGroup className="mb-2">
                  <Form.Control
                    type="text"
                    name="driftkostnad"
                    value={formData.driftkostnad || ""}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                  />
                  <InputGroup.Text>Kr</InputGroup.Text>
                </InputGroup>
              </div>
            </>
          )}

          {formData.propertyType === "bostadsratt" && (
            <>
              <div className={`mb-2 ${highlightField === 'avgift' ? 'field-highlight' : ''}`} style={{
                padding: '0.5rem',
                borderRadius: '6px'
              }}>
                <Form.Label>Månadsavgift</Form.Label>
                <InputGroup className="mb-2">
                  <Form.Control
                    type="text"
                    name="monthlyCost"
                    value={formData.monthlyCost || ""}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                  />
                  <InputGroup.Text>Kr</InputGroup.Text>
                </InputGroup>
              </div>
              <div className={`mb-2 ${highlightField === 'driftkostnad' ? 'field-highlight' : ''}`} style={{
                padding: '0.5rem',
                borderRadius: '6px'
              }}>
                <Form.Label>Driftkostnad per månad</Form.Label>
                <InputGroup className="mb-2">
                  <Form.Control
                    type="text"
                    name="driftkostnad"
                    value={formData.driftkostnad || ""}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                  />
                  <InputGroup.Text>Kr</InputGroup.Text>
                </InputGroup>
              </div>
            </>
          )}
        </Form>
          </div>
        </Collapse>
      </div>

      {/* Egen Insats */}
      <Collapse in={!isCollapsed}>
        <div>
        <Card>
          <Card.Body className="mb-2">
            <h6>Kontantinsats</h6>
            <Form.Text className="text-muted d-block mb-2">
              Standard från april 2026: 10% av köpeskillingen
            </Form.Text>
            <Tabs
              activeKey={activeTab} // Ensure it's bound to state
              onSelect={(key) => handleTabChange(key)} // Ensure correct event handling
              className="mb-2"
              justify
            >
              <Tab eventKey="fixed" title="Fast">
                <InputGroup className="mb-1">
                  <InputGroup.Text>SEK</InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="egenInsats"
                    value={formData.egenInsats || ""}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder={details.utgangspris ? formatCurrency(details.utgangspris * 0.10) : ""}
                  />
                </InputGroup>
                {isEgenInsatsManuallyEdited && (
                  <div className="mt-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={resetEgenInsats}
                      className="w-100"
                    >
                      Återställ till 10%
                    </Button>
                  </div>
                )}
              </Tab>
              <Tab eventKey="percentage" title="%">
                <Form.Group className="mb-2">
                  <Form.Label>Egen Insats ({percentageValue}%)</Form.Label>
                  <Form.Range
                    value={percentageValue}
                    onChange={(e) => setPercentageValue(e.target.value)}
                  />
                </Form.Group>
              </Tab>
            </Tabs>
            </Card.Body>
        </Card>
        </div>
      </Collapse>

      {/* Amortering */}
      <Collapse in={!isCollapsed}>
        <div>
        <Card>
          <Card.Body className="mb-2">
            <h6>Amortering per månad</h6>
            {amortInfo && (
              <div className="mb-3">
                <small className="text-muted d-block mb-2">
                  <strong>Total amortering:</strong> {amortInfo.totalAmortizationRate}% per år<br/>
                  <strong>Minimum månadsbelopp:</strong> {formatCurrency(amortInfo.calculatedAmortering)} kr/månad
                </small>
                
              </div>
            )}

            <Tabs
              activeKey={amortizationTab}
              onSelect={(key) => setAmortizationTab(key)}
              className="mb-2"
              justify
            >
              <Tab eventKey="fixed" title="Fast">
                <InputGroup className="mb-1">
                  <InputGroup.Text>SEK</InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="amortering"
                    value={formData.amortering}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder={amortInfo ? formatCurrency(amortInfo.calculatedAmortering) : ""}
                  />
                </InputGroup>
              </Tab>
              <Tab eventKey="percentage" title="%">
                <Form.Group className="mb-2">
                  <Form.Label>Amortering ({amortizationPercentage}% per år)</Form.Label>
                  <Form.Range
                    min="0"
                    max="5"
                    step="0.1"
                    value={amortizationPercentage}
                    onChange={(e) => setAmortizationPercentage(e.target.value)}
                  />
                </Form.Group>
              </Tab>
            </Tabs>
            {amortInfo && getValidationWarning() && (
              <div className="mt-2">
                <small className="text-danger">
                  {getValidationWarning()}
                </small>
              </div>
            )}
            
            <Accordion className="mt-3">
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <small className="d-flex align-items-center">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 16v-4"></path>
                      <path d="M12 8h.01"></path>
                    </svg>
                    Förklaring av amorteringsregler
                  </small>
                </Accordion.Header>
                <Accordion.Body>
                  <small className="text-muted">
                    <em>Belåningsgrad:</em> Lån / bostadens värde<br/>
                    <br/>
                    <strong>Amorteringskrav från belåningsgrad (april 2026):</strong><br/>
                    • Över 70%: Minst 2% per år<br/>
                    • 50-70%: Minst 1% per år<br/>
                    • Under 50%: Inget krav<br/>
                    <br/>
                    {amortInfo && (
                      <>
                        <strong>Din beräkning:</strong><br/>
                        <strong>Belåningsgrad:</strong> {amortInfo.ltvRatio}% → {amortInfo.ltvRuleText}<br/>
                        <strong>Resultat:</strong> {amortInfo.totalAmortizationRate}% total amortering per år<br/>
                        <br/>
                      </>
                    )}
                  </small>
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <small className="d-flex align-items-center">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 16v-4"></path>
                      <path d="M12 8h.01"></path>
                    </svg>
                    Omvärdering av bostad (5-årsregeln)
                  </small>
                </Accordion.Header>
                <Accordion.Body>
                  <small className="text-muted">
                    <strong>Från april 2026:</strong> Bostaden kan endast omvärderas var 5:e år från köp eller senaste omvärdering.<br/>
                    <br/>
                    Detta påverkar möjligheten att höja bolån eller ändra amorteringskrav på grundval av ökad bostadsvärde. Du kan inte omvärdera tidigare än 5 år efter köp eller senaste omvärdering, även om bostadsvärdena stiger.
                  </small>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Card.Body>
        </Card>
        </div>
      </Collapse>

      {/* Interest Information */}
      <Collapse in={!isCollapsed}>
        <div>
        <Card>
          <Card.Body className="mb-2">
            <h6>Låneinformation</h6>
            <Form.Group className="mb-2">
              <Form.Label>
                Bolåneränta ({parseFloat(formData.mortgageInterestRate).toFixed(2)}%)
              </Form.Label>
              <Form.Range
                min="0.5"
                max="8.0"
                step="0.05"
                value={formData.mortgageInterestRate}
                onChange={(e) => setFormData({
                  ...formData,
                  mortgageInterestRate: e.target.value
                })}
              />
              <div className="d-flex justify-content-between text-muted small">
                <span>0.5%</span>
                <span>8.0%</span>
              </div>
            </Form.Group>
          </Card.Body>
        </Card>
        </div>
      </Collapse>
    </div>
  );
};

export default InputForm;